import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "@i18n";
import { useLang } from "@/common/Contexts/LanguageContext";
import useApi from "@hooks/useApi";
import ResponsivePostGrid from "../../../../../components/shared/Grid/ResponsivePostGrid";
import PriceFilterChips from "./PriceFilterChips";
import InstallmentSectionSkeleton from "./InstallmentSectionSkeleton";
import BanksNewsBar from "@/components/BanksNewsBar/BanksNewsBar";
import { api__doSearch_with_params } from "@/apis/search/search";
var formatTitleWithBoldLastWords = function formatTitleWithBoldLastWords(title) {
  var words = title.split(" ");
  if (words.length <= 2) {
    return /*#__PURE__*/React.createElement("span", {
      className: "font-bold"
    }, title);
  }
  var lastTwoWords = words.slice(-2).join(" ");
  var firstPart = words.slice(0, -2).join(" ");
  return /*#__PURE__*/React.createElement(React.Fragment, null, firstPart, " ", /*#__PURE__*/React.createElement("span", {
    className: "font-bold"
  }, lastTwoWords));
};

// Component Props

// Main Component
var InstallmentSection = function InstallmentSection(_ref) {
  var _installmentData$post;
  var installmentData = _ref.installmentData,
    referral_location = _ref.referral_location,
    _ref$sectionVariant = _ref.sectionVariant,
    sectionVariant = _ref$sectionVariant === void 0 ? "blue" : _ref$sectionVariant;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var lang = useLang();
  var sectionType = "finance_home";
  if (!(installmentData !== null && installmentData !== void 0 && (_installmentData$post = installmentData.posts) !== null && _installmentData$post !== void 0 && _installmentData$post.length)) return null;
  var priceRanges = installmentData.filters || [];
  var firstRange = priceRanges[0] || null;
  var _useState = useState(firstRange),
    selectedRange = _useState[0],
    setSelectedRange = _useState[1];
  var _useState2 = useState(installmentData.posts),
    displayedPosts = _useState2[0],
    setDisplayedPosts = _useState2[1];
  var _useState3 = useState(false),
    isLoadingFiltered = _useState3[0],
    setIsLoadingFiltered = _useState3[1];
  var hasSkippedInitialFetchRef = useRef(false);

  // API call for filtered results
  var _useApi = useApi([selectedRange], /*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee() {
      return _regenerator().w(function (_context) {
        while (1) switch (_context.n) {
          case 0:
            if (hasSkippedInitialFetchRef.current) {
              _context.n = 1;
              break;
            }
            hasSkippedInitialFetchRef.current = true;
            return _context.a(2, null);
          case 1:
            if (selectedRange) {
              _context.n = 2;
              break;
            }
            return _context.a(2, null);
          case 2:
            return _context.a(2, api__doSearch_with_params({
              req: null,
              page: 1,
              filters: {
                monthly_installment_from: selectedRange.from,
                monthly_installment_to: selectedRange.to
              },
              size: 4,
              includes: []
            }));
        }
      }, _callee);
    })), null, null),
    _useApi2 = _slicedToArray(_useApi, 3),
    loading = _useApi2[0],
    error = _useApi2[1],
    searchData = _useApi2[2];

  // Update displayed posts when search data changes
  useEffect(function () {
    var _searchData$posts;
    if (!selectedRange) {
      setDisplayedPosts(installmentData.posts);
      setIsLoadingFiltered(false);
    } else if (searchData !== null && searchData !== void 0 && (_searchData$posts = searchData.posts) !== null && _searchData$posts !== void 0 && _searchData$posts.length) {
      setDisplayedPosts(searchData.posts);
      setIsLoadingFiltered(false);
    }
  }, [searchData, selectedRange, installmentData.posts]);
  var handleRangeSelect = function handleRangeSelect(range) {
    var isSameRange = (selectedRange === null || selectedRange === void 0 ? void 0 : selectedRange.from) === range.from && (selectedRange === null || selectedRange === void 0 ? void 0 : selectedRange.to) === range.to;
    setSelectedRange(isSameRange ? null : range);
    setIsLoadingFiltered(!isSameRange);
  };
  var buildFilterUrl = function buildFilterUrl(_ref3) {
    var range = _ref3.range,
      referral_location = _ref3.referral_location;
    if (!range) return "/filters";
    var params = new URLSearchParams({
      monthly_installment_from: range.from.toString(),
      monthly_installment_to: range.to.toString()
    });
    return "/filters?".concat(params.toString(), "#referral_location=").concat(referral_location, "&is_filter_search=1");
  };
  var ctaLink = buildFilterUrl({
    range: selectedRange,
    referral_location: referral_location
  });
  var sectionBgColor = sectionVariant === "orange" ? "before:bg-[#FFF2EE]" : "before:bg-[#F1F6FC]";
  var isLoading = loading || isLoadingFiltered;
  return /*#__PURE__*/React.createElement("section", {
    className: twMerge("relative before:block before:content-[''] before:w-full before:h-[60%] before:absolute before:top-0 before:left-0 before:z-[-1] lg:before:h-[500px] lg:pb-0", sectionBgColor)
  }, /*#__PURE__*/React.createElement("div", {
    className: "py-6 flex flex-col gap-3 z-[10] max-w-[540px] md:max-w-[720px] lg:max-w-[960px] xl:max-w-[1275px] 2xl:max-w-[1320px] mx-auto lg:gap-[30px] lg:pt-[88px] lg:pb-0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-3 justify-center lg:w-[580px] lg:mx-auto px-4 lg:px-0 lg:mt-0 lg:gap-4"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "text-primary text-lg text-center lg:text-3xl min-h-[1.75rem] lg:min-h-[2.25rem]"
  }, formatTitleWithBoldLastWords(installmentData.title || ""))), /*#__PURE__*/React.createElement(BanksNewsBar, {
    speed: "fast",
    direction: lang === "en" ? "left" : "right",
    banks: installmentData === null || installmentData === void 0 ? void 0 : installmentData.logos,
    pauseOnHover: false
  }), /*#__PURE__*/React.createElement(PriceFilterChips, {
    ranges: priceRanges,
    onRangeSelect: handleRangeSelect,
    selectedRange: selectedRange
  }), isLoading ? /*#__PURE__*/React.createElement(InstallmentSectionSkeleton, {
    numberOfCards: 4
  }) : /*#__PURE__*/React.createElement(ResponsivePostGrid, {
    posts: displayedPosts,
    ctaLink: ctaLink,
    sectionType: sectionType
  })), /*#__PURE__*/React.createElement("div", {
    className: "hidden md:flex md:items-center md:justify-center md:mt-[56px]"
  }, /*#__PURE__*/React.createElement(Link, {
    to: ctaLink,
    className: "rounded bg-[#FE561C] w-[250px] h-[48px] p-4 text-center text-white text-sm font-bold leading-4 outline-none border-none cursor-pointer hover:text-white",
    id: "InstallmentSection"
  }, t("اعرض الكل", {
    ns: "web_common"
  }))));
};
export default InstallmentSection;